import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ContentPageLayout from "../components/ContentPageLayout";
import BoardPreview from "../components/BoardPreview";
import JsonLd, { breadcrumbList } from "../components/JsonLd";
import { STORYBOARD_TEMPLATE_PAGES_BY_SLUG } from "../content/storyboardTemplates";
import { GUIDES_BY_SLUG } from "../content/guides";
import { findBoardLayout, findFrameAspect, normalizeStoryboard } from "../constants/storyboard";
import { findPageSizePreset } from "../constants/pageDimensions";
import { downloadBlankBoardPDF } from "../utils/blankBoard";
import { trackEvent } from "../utils/analytics";

const StoryboardTemplatePage = () => {
  const { slug } = useParams();
  const template = STORYBOARD_TEMPLATE_PAGES_BY_SLUG[slug];
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (template) trackEvent("storyboard_template_viewed", { slug });
  }, [slug, template]);

  if (!template) {
    return (
      <ContentPageLayout>
        <main style={styles.main}>
          <h1 style={styles.title}>Template not found</h1>
          <p style={styles.lede}>
            That template doesn't exist. Browse all{" "}
            <Link to="/storyboard-templates" style={styles.inlineLink}>storyboard templates</Link>.
          </p>
        </main>
      </ContentPageLayout>
    );
  }

  const board = normalizeStoryboard(template.seed.storyboard);
  const layout = findBoardLayout(board.layoutKey);
  const specs = [
    ["Frame aspect ratio", findFrameAspect(board.aspectKey).label],
    ["Frames per page", `${layout.columns * layout.rows} (${layout.columns} × ${layout.rows})`],
    ["Notes", layout.notes === "right" ? "Beside each frame" : "Under each frame"],
    ["Paper", findPageSizePreset(template.seed.pageSizeKey).label],
    ["Safe areas", board.safeAreas ? "Action safe 93%, title safe 90%" : "Off (one click to turn on)"],
    ["Best for", template.useCase],
  ];
  const related = (template.related || []).map((s) => STORYBOARD_TEMPLATE_PAGES_BY_SLUG[s]).filter(Boolean);
  const guides = (template.guides || []).map((s) => GUIDES_BY_SLUG[s]).filter(Boolean);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadBlankBoardPDF(template);
    } catch (e) {
      console.error("Failed to build template PDF:", e);
      window.alert("Couldn't build the PDF. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ContentPageLayout>
      <JsonLd data={breadcrumbList([
        { name: "Storyboard maker", path: "/storyboard" },
        { name: "Storyboard templates", path: "/storyboard-templates" },
        { name: template.title, path: `/storyboard-templates/${template.slug}` },
      ])} />
      <main style={styles.main}>
        <nav style={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/storyboard" style={styles.inlineLink}>Storyboard maker</Link>
          <span style={styles.crumbSep}>/</span>
          <Link to="/storyboard-templates" style={styles.inlineLink}>Templates</Link>
          <span style={styles.crumbSep}>/</span>
          <span>{template.name}</span>
        </nav>

        <h1 style={styles.title}>{template.title}</h1>
        <p style={styles.lede}>{template.description}</p>

        <div style={styles.previewWrap}>
          <BoardPreview
            seed={template.seed}
            title={`Blank ${template.title.toLowerCase()} page`}
            style={styles.previewPaper}
          />
        </div>

        <div style={styles.ctaRow}>
          <Link
            to={`/storyboard/editor?template=${template.slug}`}
            style={styles.primaryCta}
            onClick={() => trackEvent("storyboard_cta", { from: "template_page", slug: template.slug })}
          >
            Open in the editor
          </Link>
          <button type="button" style={styles.secondaryCta} onClick={handleDownload} disabled={downloading}>
            {downloading ? "Building PDF…" : "Download blank PDF"}
          </button>
        </div>

        <table style={styles.specs}>
          <tbody>
            {specs.map(([label, value]) => (
              <tr key={label}>
                <th scope="row" style={styles.specLabel}>{label}</th>
                <td style={styles.specValue}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <article className="guide-content" style={styles.article}>{template.body}</article>

        {(related.length > 0 || guides.length > 0) && (
          <aside style={styles.related}>
            {related.length > 0 && (
              <>
                <h2 style={styles.relatedTitle}>More storyboard templates</h2>
                <ul style={styles.relatedList}>
                  {related.map((r) => (
                    <li key={r.slug} style={styles.relatedItem}>
                      <Link to={`/storyboard-templates/${r.slug}`} style={styles.relatedLink}>{r.title}</Link>
                      <span style={styles.relatedSummary}> — {r.summary}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {guides.length > 0 && (
              <>
                <h2 style={styles.relatedTitle}>Guides</h2>
                <ul style={styles.relatedList}>
                  {guides.map((g) => (
                    <li key={g.slug} style={styles.relatedItem}>
                      <Link to={`/guides/${g.slug}`} style={styles.relatedLink}>{g.title}</Link>
                      <span style={styles.relatedSummary}> — {g.summary}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </aside>
        )}
      </main>
    </ContentPageLayout>
  );
};

const button = {
  display: "inline-block",
  padding: "13px 26px",
  fontSize: 15,
  fontWeight: 700,
  textDecoration: "none",
  textTransform: "uppercase",
  letterSpacing: 1,
  border: "2px solid #1a1a1a",
  fontFamily: "'Georgia', serif",
  cursor: "pointer",
};

const styles = {
  main: {
    flex: 1,
    width: "100%",
    maxWidth: 820,
    margin: "0 auto",
    padding: "40px 16px 56px",
    boxSizing: "border-box",
  },
  breadcrumb: {
    fontSize: 13,
    color: "#999",
    marginBottom: 20,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  crumbSep: { margin: "0 8px" },
  inlineLink: { color: "#00a4e4", textDecoration: "none" },
  title: {
    fontSize: "clamp(28px, 5.5vw, 38px)",
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "0 0 12px",
    color: "#1a1a1a",
  },
  lede: { fontSize: 17, color: "#555", margin: "0 0 24px" },
  previewWrap: { maxWidth: 640, margin: "0 auto 24px" },
  previewPaper: { border: "2px solid #1a1a1a", boxShadow: "6px 6px 0 rgba(0, 0, 0, 0.18)" },
  ctaRow: { display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginBottom: 32 },
  primaryCta: { ...button, background: "#1a1a1a", color: "#f5f0e8" },
  secondaryCta: { ...button, background: "#ffe600", color: "#1a1a1a" },
  specs: {
    width: "100%",
    borderCollapse: "collapse",
    border: "2px solid #1a1a1a",
    background: "#fffdf6",
    marginBottom: 32,
    fontSize: 15,
  },
  specLabel: {
    textAlign: "left",
    padding: "8px 12px",
    borderBottom: "1px solid rgba(26, 26, 26, 0.15)",
    width: "38%",
    fontWeight: 700,
    verticalAlign: "top",
  },
  specValue: { padding: "8px 12px", borderBottom: "1px solid rgba(26, 26, 26, 0.15)", color: "#444" },
  article: { marginBottom: 8 },
  related: { marginTop: 40, borderTop: "2px solid #1a1a1a", paddingTop: 20 },
  relatedTitle: {
    fontSize: 14,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#999",
    margin: "0 0 12px",
  },
  relatedList: { listStyle: "none", margin: "0 0 20px", padding: 0 },
  relatedItem: { marginBottom: 10, fontSize: 15, lineHeight: 1.5 },
  relatedLink: { color: "#00a4e4", textDecoration: "none", fontWeight: 700 },
  relatedSummary: { color: "#777", fontSize: 14 },
};

export default StoryboardTemplatePage;
